import React, { FC, useState, useRef } from 'react'
import Logo from '../../assets/main_logo.jpg'
import mobileLogo from '../../assets/StonyLogo.png'
import { Row, useScreenClass, Hidden } from 'react-grid-system'
import styled from 'styled-components'
import { Button, Hamburger, Menu } from '.'
import { useOnClickOutside } from './Hamburger'
import { NavLink, Link } from 'react-router-dom'
import { NavHashLink } from 'react-router-hash-link'

const LinkWrapper = styled.div`
  .nav-link {
    color: ${(props) => props.theme.colors.black};
    font-size: var(--fs-heading-sm);
    font-weight: 500;
    margin: 0 12px;
    cursor: pointer;
    position: relative;
    transition: color 0.3s ease;
    &:hover {
      color: #990000;
    }
  }

  a:last-child {
    margin-left: 12px;
  }
`

// Styled component for the dropdown toggle that includes a caret
const DropdownToggle = styled.button<{ open: boolean }>`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  font-size: var(--fs-heading-sm);
  font-weight: 500;
  font-family: var(--font-sans);
  cursor: pointer;
  color: ${(props) => props.theme.colors.black};
  margin: 0 12px;
  &:hover {
    color: #990000;
  }
  &:focus-visible {
    outline: 3px solid #990000;
    outline-offset: 4px;
    border-radius: 4px;
  }
  &::after {
    content: ' ▼';
    font-size: 12px;
    margin-left: 4px;
    transition: transform 0.3s ease;
    transform: ${(props) => (props.open ? 'rotate(180deg)' : 'rotate(0)')};
  }
`

const Dropdown = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownContent = styled.div<{ open: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  border: 1px solid #eee;
  border-radius: 4px;
  min-width: 180px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  opacity: ${(props) => (props.open ? 1 : 0)};
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  transform: ${(props) => (props.open ? 'translateY(0)' : 'translateY(10px)')};
  transition: opacity 0.3s ease, transform 0.3s ease, visibility 0.3s;

  a {
    display: block;
    padding: 12px 16px;
    color: ${(props) => props.theme.colors.black};
    text-decoration: none;
    font-size: var(--fs-body-sm);
    transition: background-color 0.3s ease;
    &:hover {
      background-color: #f7f7f7;
    }
    &:focus-visible {
      outline: 3px solid #990000;
      outline-offset: -3px;
      background-color: #f7f7f7;
    }
  }
`

const Nav = styled.nav`
  padding: 8px 3rem;
  background-color: white;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
`

const activeStyles = {
  color: '#990000',
  fontWeight: 700,
  letterSpacing: '1px',
  transition: 'font-weight 0.3s',
}

const NavBar: FC = () => {
  const screenClass = useScreenClass()
  const isDesktop = ['lg', 'xl'].includes(screenClass)
  const [openMenu, setOpenMenu] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const burger = useRef(null)
  useOnClickOutside(burger, () => setOpenMenu(false))

  return (
    <Nav>
      <Row justify="between" align="center">
        <Link to="/">
          {['lg', 'xl'].includes(screenClass) ? (
            <img
              src={Logo}
              alt="Stony Brook University Logo"
              style={{ width: '80%' }}
            />
          ) : (
            <img
              src={mobileLogo}
              alt="Stony Brook University Logo"
              style={{ width: '90px' }}
            />
          )}
        </Link>
        <Hidden lg xl>
          <div ref={burger}>
            <Hamburger open={openMenu} setOpen={setOpenMenu} />
            <Menu open={openMenu} />
          </div>
        </Hidden>
        <Hidden xs sm md>
          <LinkWrapper>
            {/* About Us Dropdown */}
            <Dropdown
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <DropdownToggle
                open={dropdownOpen}
                className="nav-link"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                onClick={() => setDropdownOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === 'Escape') setDropdownOpen(false) }}
              >
                About Us
              </DropdownToggle>
              <DropdownContent open={dropdownOpen} role="menu">
                <NavLink to="/faculty" className="nav-link" activeStyle={activeStyles} role="menuitem">
                  Faculty
                </NavLink>
                <NavLink to="/guiding-principles" className="nav-link" activeStyle={activeStyles} role="menuitem">
                  Guiding Principles
                </NavLink>
                <a
                  href="https://docs.google.com/document/d/16Cvxv-A0cWn821JXLTZbJ_E6qkxtgUcfhFwr9spFxCI/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link"
                  role="menuitem"
                  aria-label="Syllabus (opens in new tab)"
                >
                  Syllabus
                </a>
                <NavLink to="/alumni" className="nav-link" activeStyle={activeStyles} role="menuitem">
                  Alumni
                </NavLink>
                <NavLink to="/shows" className="nav-link" activeStyle={activeStyles} role="menuitem">
                  Our Shows
                </NavLink>
              </DropdownContent>
            </Dropdown>
            {/* Other Topnav Links */}
            <NavLink to="/resources" className="nav-link" activeStyle={activeStyles}>
              Resources
            </NavLink>
            <NavLink to="/guests" className="nav-link" activeStyle={activeStyles}>
              Guests
            </NavLink>
            <NavHashLink to="/#contact" className="nav-link" activeStyle={activeStyles}>
              Contact
            </NavHashLink>
            <NavLink to="/apply" className="nav-link" activeStyle={activeStyles}>
              FAQs
            </NavLink>
            <Button
              external
              variant="red"
              path="https://stonybrookuniversity.co1.qualtrics.com/jfe/form/SV_3aqRXNG82X43wDI"
            >
              Apply Now
            </Button>
          </LinkWrapper>
        </Hidden>
      </Row>
    </Nav>
  )
}

export default NavBar
